import {
  AutoComplete,
  DatePicker,
  InputNumber,
  Input,
  Select,
  Tooltip,
} from "antd";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CopyOutlined,
  LoadingOutlined,
  FlagFilled,
  EyeInvisibleOutlined,
  FlagOutlined,
  FolderFilled,
  LockOutlined,
  PlusOutlined,
  UserAddOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import React from "react";
import dayjs from "dayjs";
import moment from "moment";
import { getEntryOriginLabel } from "utils/entry-origin";
import { UNCONFIRMED_EDIT_MESSAGE } from "./useProcurementEditFeedback";
import { FilterIconHeader } from "./FilterIconHeader";
import { getColumnFilterType } from "./procurementFilters";
import {
  getProcurementRowIdentity,
  withPendingApprovalTooltip,
} from "./procurementHelpers";
import {
  buildWorkflowLockTooltip,
  EMPTY_WORKFLOW_LOCK_STATE,
  getWorkflowLockTooltipLabels,
} from "./procurementDependencies";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const SUBMITTAL_DROPDOWN_SELECTION_MARKER =
  "__procurementSubmittalDropdownSelection";

// Parses calendar values used by the procurement date editor.
function parseCalendarDate(value) {
  if (!value) return null;

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [month, day, year] = value.split("-");
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate : null;
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate : null;
}

// Interprets backend working-day flags for the date picker.
function isWorkingDayEnabled(value) {
  return value === "1" || value === 1 || value === true;
}

// Parses holiday range boundaries used to disable dates in the calendar.
function parseHolidayRangeDate(value) {
  if (!value) return null;

  const parsedDate = moment(
    value,
    ["MM-DD-YYYY", "MM/DD/YYYY", "YYYY-MM-DD"],
    true,
  );
  return parsedDate.isValid() ? parsedDate.startOf("day") : null;
}

// Builds the disabled weekday and holiday lookup for the date picker.
function buildDisabledCalendarConfig(workingDays, nonWorkingDays) {
  const disabledWeekdays = new Set(
    DAY_NAMES.filter(
      (dayName) =>
        Object.prototype.hasOwnProperty.call(workingDays || {}, dayName) &&
        !isWorkingDayEnabled(workingDays?.[dayName]),
    ),
  );

  const disabledDates = new Set();

  (nonWorkingDays || []).forEach((holiday) => {
    const startDate = parseHolidayRangeDate(holiday?.startDate);
    const finishDate = parseHolidayRangeDate(holiday?.finishDate);

    if (!startDate || !finishDate) {
      return;
    }

    const rangeStart = startDate.isAfter(finishDate) ? finishDate : startDate;
    const rangeEnd = startDate.isAfter(finishDate) ? startDate : finishDate;
    const currentDate = rangeStart.clone();

    while (currentDate.isSameOrBefore(rangeEnd, "day")) {
      disabledDates.add(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "day");
    }
  });

  return { disabledWeekdays, disabledDates };
}

function buildEditingPresenceCellKey(entryId, field) {
  const normalizedEntryId = getProcurementRowIdentity({ entryID: entryId });
  const normalizedField = String(field || "").trim();

  if (!normalizedEntryId || !normalizedField) {
    return "";
  }

  return `${normalizedEntryId}::${normalizedField}`;
}

function getEditingPresenceForCell(options, row, field) {
  if (row?.rowType !== "submittal" || !field) {
    return null;
  }

  const cellKey = buildEditingPresenceCellKey(row?.entryID, field);
  const editingPresenceByCellKey =
    typeof options?.getEditingPresenceByCellKey === "function"
      ? options.getEditingPresenceByCellKey()
      : options?.editingPresenceByCellKey;
  const presenceList = editingPresenceByCellKey?.[cellKey];

  if (!Array.isArray(presenceList) || presenceList.length === 0) {
    return null;
  }

  const [primaryPresence, secondaryPresence, ...overflowPresences] =
    presenceList;
  const allLabels = presenceList
    .map((presence) => String(presence?.label || "").trim())
    .filter(Boolean);

  return {
    ...primaryPresence,
    count: presenceList.length,
    topBadgeLabel: primaryPresence.label,
    secondaryPresence,
    bottomBadgeLabel: secondaryPresence?.label || "",
    overflowCount: overflowPresences.length,
    hoverTitle:
      allLabels.length > 1
        ? `Users editing this cell:\n${allLabels.join("\n")}`
        : `${allLabels[0]} is editing this cell`,
  };
}

function renderCellWithEditingPresence(content, params, field, options) {
  const presence = getEditingPresenceForCell(options, params?.data, field);

  if (!presence) {
    return content;
  }

  return (
    <div
      className="procurementRemotePresenceWrapper"
      style={{
        borderColor: presence.color,
        backgroundColor: presence.backgroundColor,
      }}
      title={presence.hoverTitle}
    >
      <span
        className="procurementRemotePresenceBadge procurementRemotePresenceBadgeTop"
        style={{
          backgroundColor: presence.color,
        }}
      >
        {presence.topBadgeLabel}
      </span>
      {presence.secondaryPresence ? (
        <span
          className="procurementRemotePresenceBadge procurementRemotePresenceBadgeBottom"
          style={{
            backgroundColor: presence.secondaryPresence.color,
          }}
        >
          {presence.bottomBadgeLabel}
          {presence.overflowCount > 0 ? (
            <span className="procurementRemotePresenceOverflowCount">
              {` +${presence.overflowCount}`}
            </span>
          ) : null}
        </span>
      ) : null}
      <div className="procurementRemotePresenceInner">{content}</div>
    </div>
  );
}

function getCellEditFeedback(options, row, field) {
  if (options.accessLevel !== 1 || row?.rowType !== "submittal") return null;
  return options.getCellEditFeedback?.(getProcurementRowIdentity(row), field);
}

function renderCellWithEditFeedback(content, params, field, options) {
  const feedback = getCellEditFeedback(options, params.data, field);
  if (!feedback) return content;
  const isWorking =
    feedback.status === "saving" || feedback.status === "submitting";
  const isUnconfirmed = feedback.status === "unconfirmed";
  const label = {
    saving: "Saving…",
    submitting: "Submitting…",
    saved: "Saved",
    submitted: "Submitted",
    unconfirmed: "Unconfirmed",
  }[feedback.status];
  const message = isUnconfirmed
    ? `${UNCONFIRMED_EDIT_MESSAGE} Your entered value is still shown.`
    : feedback.status === "submitted"
      ? "Your change was submitted for manager approval."
      : label;

  return (
    <div className="procurementCellFeedback">
      <div className="procurementCellFeedbackValue">{content}</div>
      <div className="procurementCellFeedbackLine">
        <Tooltip title={message} placement="top">
          <span
            className={`procurementCellFeedbackStatus procurementCellFeedbackStatus_${feedback.status}`}
            role="status"
            aria-live="polite"
            aria-label={`${params.colDef.headerName}: ${message}`}
          >
            {isWorking ? (
              <LoadingOutlined spin />
            ) : isUnconfirmed ? (
              <ExclamationCircleFilled />
            ) : (
              <CheckCircleFilled />
            )}
            <span>{label}</span>
          </span>
        </Tooltip>
        {isUnconfirmed &&
          options.onRetryCellEdit &&
          params.colDef.editable?.(params) && (
            <button
              type="button"
              className="procurementCellFeedbackRetry"
              aria-label={`Retry ${params.colDef.headerName} change`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onKeyDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                options.onRetryCellEdit(
                  getProcurementRowIdentity(params.data),
                  field,
                );
              }}
            >
              Retry
            </button>
          )}
      </div>
    </div>
  );
}

// Renders the text editor used by editable string cells.
const StringEditor = ({ value, onValueChange, stopEditing }) => (
  <Input
    autoFocus
    variant="borderless" // Removes AntD default border
    defaultValue={value}
    onChange={(e) => onValueChange(e.target.value)}
    onBlur={() => stopEditing()}
    onPressEnter={() => stopEditing()}
    style={{ width: "100%", height: "100%", fontSize: "13px" }}
  />
);

// Finds the source submittals available to the row's current subdivision.
function getSubmittalRevisionOptions(submittalsDropDown, row) {
  const subdivisions = submittalsDropDown?.subdivisions || {};
  const availableSubmittalKeys = Object.keys(subdivisions);
  const subdivisionCandidates = [
    row?.subdivisionID,
    row?.subdivisionId,
    row?.subDiv,
    row?.subdivision,
    row?.specSection,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  const matchedSubdivisionKey = subdivisionCandidates
    .map((candidate) =>
      availableSubmittalKeys.find(
        (subdivisionKey) =>
          subdivisionKey.trim().toLowerCase() === candidate.toLowerCase(),
      ),
    )
    .find(Boolean);
  const submittalValues = matchedSubdivisionKey
    ? subdivisions[matchedSubdivisionKey]
    : [];
  const submittals = submittalsDropDown?.submittals || {};

  return (Array.isArray(submittalValues) ? submittalValues : []).map(
    (submittalValue) => {
      const source = submittals?.[submittalValue];
      const sourceRow = Array.isArray(source) ? source[0] : source;

      return {
        value: submittalValue,
        label: submittalValue,
        sourceRow,
      };
    },
  );
}

// Lets users choose a complete submittal reference while retaining free-form number entry.
const SubmittalNumberEditor = ({
  value,
  onValueChange,
  stopEditing,
  submittalsDropDown,
  data,
}) => {
  const [editorValue, setEditorValue] = React.useState(value ?? "");
  const options = React.useMemo(
    () => getSubmittalRevisionOptions(submittalsDropDown, data),
    [data, submittalsDropDown],
  );

  return (
    <AutoComplete
      autoFocus
      open
      allowClear
      value={editorValue}
      options={options}
      filterOption={(inputValue, option) =>
        String(option?.value || "")
          .toLowerCase()
          .includes(
            String(inputValue || "")
              .trim()
              .toLowerCase(),
          )
      }
      placeholder="Select or type submittal number"
      onChange={(nextValue) => {
        const normalizedValue = nextValue || "";
        setEditorValue(normalizedValue);
        onValueChange(normalizedValue);
      }}
      onSelect={(selectedValue, option) => {
        const sourceRow = option?.sourceRow;

        if (!sourceRow || typeof sourceRow !== "object") {
          return;
        }

        setEditorValue(selectedValue);
        onValueChange({
          [SUBMITTAL_DROPDOWN_SELECTION_MARKER]: true,
          sourceRow,
        });
        stopEditing();
      }}
      onBlur={() => {
        window.setTimeout(stopEditing, 0);
      }}
      style={{ width: "100%", height: "100%" }}
      popupMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 260 }}
    >
      <Input
        variant="borderless"
        style={{ width: "100%", height: "100%", fontSize: "13px" }}
      />
    </AutoComplete>
  );
};

// Renders the numeric editor used by editable int and float cells.
const NumberEditor = ({ value, onValueChange, stopEditing }) => (
  <InputNumber
    autoFocus
    variant="borderless"
    defaultValue={value}
    onChange={(val) => onValueChange(val)}
    onBlur={() => stopEditing()}
    onPressEnter={() => stopEditing()}
    style={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
    }}
  />
);

// Renders the date picker editor with project working-day rules applied.
const DateEditor = ({
  value,
  onValueChange,
  stopEditing,
  workingDays,
  nonWorkingDays,
}) => {
  const pickerValue = parseCalendarDate(value);
  const calendarConfig = React.useMemo(
    () => buildDisabledCalendarConfig(workingDays, nonWorkingDays),
    [workingDays, nonWorkingDays],
  );

  return (
    <div className="procurementDateEditor">
      <DatePicker
        autoFocus
        open={true}
        size="large"
        inputReadOnly
        value={pickerValue}
        format="MM-DD-YYYY"
        disabledDate={(current) => {
          if (!current) {
            return false;
          }

          const formattedDate = current.format("YYYY-MM-DD");
          const dayName = current.format("dddd");

          return (
            calendarConfig.disabledWeekdays.has(dayName) ||
            calendarConfig.disabledDates.has(formattedDate)
          );
        }}
        onChange={(date) => {
          onValueChange(date ? date.format("YYYY-MM-DD") : null);
          stopEditing();
        }}
        style={{ width: "100%", height: "100%" }}
        popupStyle={{ zIndex: 2000 }}
      />
    </div>
  );
};

// Renders the schedule selector editor for schedule-linked rows.
const ScheduleIdEditor = ({
  value,
  onValueChange,
  stopEditing,
  scheduleOptions,
}) => (
  <div className="procurementDateEditor">
    <Select
      autoFocus
      open={true}
      showSearch
      allowClear
      value={value || undefined}
      options={scheduleOptions}
      optionFilterProp="label"
      placeholder="Select Schedule ID"
      onChange={(nextValue) => {
        onValueChange(nextValue || "");
        stopEditing();
      }}
      onBlur={() => stopEditing()}
      style={{ width: "100%", height: "100%" }}
      dropdownMatchSelectWidth={false}
      popupMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 260 }}
    />
  </div>
);

// Returns the appropriate inline editor for a column type.
function getCellEditor(type) {
  switch (type) {
    case "int":
      return NumberEditor;
    case "date":
      return DateEditor;
    case "string":
    default:
      return StringEditor;
  }
}

// Wraps the date editor with the current calendar disabling rules.
function createDateCellEditor(workingDays, nonWorkingDays) {
  return function WrappedDateEditor(props) {
    return (
      <DateEditor
        {...props}
        workingDays={workingDays}
        nonWorkingDays={nonWorkingDays}
      />
    );
  };
}

// Wraps the schedule editor with the latest schedule options.
function createScheduleIdCellEditor(scheduleOptions) {
  return function WrappedScheduleIdEditor(props) {
    return (
      <ScheduleIdEditor {...props} scheduleOptions={scheduleOptions || []} />
    );
  };
}

// Wraps the number picker with the latest subdivision-scoped submittal data.
function createSubmittalNumberCellEditor(submittalsDropDown) {
  return function WrappedSubmittalNumberEditor(props) {
    return (
      <SubmittalNumberEditor
        {...props}
        submittalsDropDown={submittalsDropDown || {}}
      />
    );
  };
}

function formatScheduleIdDisplay(value, scheduleOptions) {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchedOption = (scheduleOptions || []).find(
    (option) => String(option?.value || "").trim() === normalizedValue,
  );
  const optionLabel = String(matchedOption?.label || "").trim();

  return optionLabel || normalizedValue;
}

// Renders the active sort indicator for sortable headers.
function renderSortIndicator(sortState) {
  if (!sortState) {
    return null;
  }

  return (
    <span style={{ color: "#1777ff", fontSize: 10, lineHeight: 1 }}>
      {sortState === "asc" ? "▲" : "▼"}
    </span>
  );
}

// Renders the shared checkbox control used for smart selection.
function SelectionCheckboxControl({ state, onToggle, disabled = false }) {
  const checked = state === "all";
  const indeterminate = state === "some";

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        minWidth: 24,
        height: "100%",
        cursor: disabled ? "not-allowed" : "pointer",
        flexShrink: 0,
        opacity: disabled ? 0.45 : 1,
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (disabled) {
          return;
        }
        onToggle?.();
      }}
    >
      <div
        style={{
          width: 15,
          height: 15,
          borderRadius: 4,
          border: checked || indeterminate ? "none" : "1.5px solid #cbd5e1",
          background: checked
            ? "#3b82f6"
            : indeterminate
              ? "#93c5fd"
              : "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s ease",
          flexShrink: 0,
        }}
      >
        {checked && (
          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path
              d="M1 3.5L3.5 6L8 1"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {indeterminate && (
          <svg width="8" height="2" viewBox="0 0 8 2" fill="none">
            <path
              d="M1 1H7"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        )}
      </div>
    </div>
  );
}

// Renders the hierarchy header with expand-all and sort controls.
const HierarchyHeader = ({
  displayName,
  description,
  allExpanded,
  onToggleAllGroups,
  sortState,
  onToggleSort,
}) => {
  const isSortable = typeof onToggleSort === "function";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        height: "100%",
      }}
    >
      <Tooltip
        title={allExpanded ? "Collapse all divisions" : "Expand all divisions"}
        placement="top"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggleAllGroups?.();
          }}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            color: "#3a6fa8",
            cursor: "pointer",
          }}
        >
          {allExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </button>
      </Tooltip>

      <div
        onClick={
          isSortable
            ? (event) => {
                event.stopPropagation();
                onToggleSort();
              }
            : undefined
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          minWidth: 0,
          cursor: isSortable ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        <Tooltip title={description} placement="top">
          <span
            style={{
              cursor: isSortable ? "pointer" : "default",
              minWidth: 0,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              whiteSpace: "normal",
              wordBreak: "normal",
              overflowWrap: "normal",
              lineHeight: 1.15,
            }}
          >
            {displayName}
          </span>
        </Tooltip>
        {renderSortIndicator(sortState)}
      </div>
    </div>
  );
};

// Measures header text so column widths can be sized more intentionally.
function measureTextWidth(text, font = "700 11px sans-serif") {
  if (typeof document === "undefined") return 120;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = font;
  const label = String(text).toUpperCase();
  const letterSpacing = Math.max(label.length - 1, 0) * 0.55;
  // Leave room for cell padding, the filter button, and the sort indicator.
  return Math.ceil(ctx.measureText(label).width + letterSpacing) + 80;
}

// Normalizes entry-origin values before display or comparison.
function normalizeOriginValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

// Maps entry-origin values to the indicator color used in the grid.
function getEntryOriginColor(origin) {
  return normalizeOriginValue(origin) === "Procore"
    ? "#f08a24"
    : normalizeOriginValue(origin) === "Ex-Procore"
      ? "#FF0000"
      : "#1777FF";
}

// Renders the entry-origin indicator cell for submittal rows.
function EntryOriginCell(params) {
  const row = params.data;
  if (row?.rowType !== "submittal") {
    return null;
  }

  const origin = normalizeOriginValue(params.value || row.entryOrigin);
  if (!origin) {
    return null;
  }

  const color = getEntryOriginColor(origin);

  return (
    <Tooltip title={getEntryOriginLabel(origin)} placement="right">
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 10,
          height: 10,
          borderRadius: 999,
          background: color,
          boxShadow: `0 0 0 3px ${color}1f`,
        }}
      />
    </Tooltip>
  );
}

function getWorkflowLockState(options, row) {
  const entryId = getProcurementRowIdentity(row);

  if (!entryId) {
    return EMPTY_WORKFLOW_LOCK_STATE;
  }

  return (
    options?.dependencyStateByEntryId?.[entryId] || EMPTY_WORKFLOW_LOCK_STATE
  );
}

function shouldSuppressTitleMouseHandling(event) {
  const target = event?.target;

  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest(
      ".procurementSubmittalActions, .procurementCellFeedbackRetry",
    ),
  );
}

function renderWorkflowLockTooltipContent(lockState) {
  const labels = getWorkflowLockTooltipLabels(lockState);

  if (!labels.length) {
    return buildWorkflowLockTooltip(lockState);
  }

  return (
    <div>
      <div>Blocked until these predecessor submittals are completed:</div>
      <ul
        style={{
          margin: "6px 0 0",
          paddingLeft: 18,
        }}
      >
        {labels.map((label) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    </div>
  );
}

// Returns the sizing rules for each procurement column.
function getColumnSizing(field, col) {
  const labelWidth = measureTextWidth(col?.label || "");
  const isProcoreSyncedColumn =
    field === "specType" ||
    field === "specResponse" ||
    field === "responsibleContractor";

  if (field === "specTitle") {
    return { minWidth: Math.max(labelWidth, 340), flex: 1.9 };
  }

  if (field === "specNumber") {
    return { minWidth: Math.max(labelWidth, 130), flex: 0.85 };
  }

  if (isProcoreSyncedColumn) {
    return {
      minWidth: Math.max(labelWidth + 24, 180),
      flex: field === "responsibleContractor" ? 1.1 : 0.9,
    };
  }

  if (col?.type === "int") {
    // Short labels like "LT", "RT" with small numeric values — keep very compact
    if (labelWidth <= 40) {
      return { minWidth: Math.max(labelWidth, 52), flex: 0.2 };
    }
    if (labelWidth <= 70) {
      return { minWidth: Math.max(labelWidth, 75), flex: 0.25 };
    }
    return { minWidth: Math.max(labelWidth, 120), flex: 0.35 };
  }

  if (col?.type === "float") {
    if (labelWidth <= 40)
      return { minWidth: Math.max(labelWidth, 80), flex: 0.22 };
    if (labelWidth <= 70)
      return { minWidth: Math.max(labelWidth, 100), flex: 0.28 };
    return { minWidth: Math.max(labelWidth, 110), flex: 0.4 };
  }

  if (col?.type === "date") {
    return { minWidth: Math.max(labelWidth, 125), flex: 0.8 };
  }

  if (labelWidth <= 120) {
    return { minWidth: Math.max(labelWidth, 120), flex: 0.4 };
  }

  return { minWidth: Math.max(labelWidth, 130), flex: 1 };
}

const TREE_LINE_COLOR = "#c6d6eb";
const TREE_GUIDE_STEP = 18;
const GROUP_SUMMARY_META = [
  { key: "draft", label: "Draft", className: "procurementSummaryDraft" },
  { key: "pending", label: "Pending", className: "procurementSummaryPending" },
  { key: "overdue", label: "Overdue", className: "procurementSummaryOverdue" },
  {
    key: "completed",
    label: "Completed",
    className: "procurementSummaryCompleted",
  },
];
const WARNING_STATUS_FIELDS = [
  "contractIssuedRelease",
  "submittalIssued",
  "submittalReturned",
];

// Checks whether a submittal is currently flagged as critical path.
function isCriticalPathItem(value) {
  if (value === true) return true;
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }
  return false;
}

// Draws the tree connector lines for nested submittal families.
function renderTreeGuides(row) {
  if (row.rowType !== "submittal") return null;

  const treeDepth = row.treeDepth || 0;
  const guideWidth = treeDepth * TREE_GUIDE_STEP;

  if (guideWidth <= 0) return null;

  const currentLineLeft = (treeDepth - 1) * TREE_GUIDE_STEP + 8;

  return (
    <div
      style={{
        width: guideWidth,
        minWidth: guideWidth,
        position: "relative",
        height: "100%",
        flexShrink: 0,
      }}
    >
      {(row.treeLines || []).map((shouldContinue, index) =>
        shouldContinue ? (
          <span
            key={`ancestor-line-${index}`}
            style={{
              position: "absolute",
              left: `${index * TREE_GUIDE_STEP + 8}px`,
              top: 0,
              bottom: 0,
              width: 1,
              background: TREE_LINE_COLOR,
            }}
          />
        ) : null,
      )}

      <span
        style={{
          position: "absolute",
          left: `${currentLineLeft}px`,
          top: 0,
          height: row.isLastSibling ? "50%" : "100%",
          width: 1,
          background: TREE_LINE_COLOR,
        }}
      />

      <span
        style={{
          position: "absolute",
          left: `${currentLineLeft}px`,
          top: "50%",
          width: `${TREE_GUIDE_STEP - 2}px`,
          height: 1,
          background: TREE_LINE_COLOR,
        }}
      />
    </div>
  );
}

// Renders the hierarchy-aware title cell for groups and submittals.
function HierarchyTitleCell(params, options) {
  const row = params.data;

  if (!row) return params.value || null;

  const isDivision = row.rowType === "division";
  const isSubdivision = row.rowType === "subdivision";
  const isSubmittal = row.rowType === "submittal";
  const isGroup = isDivision || isSubdivision;
  const isCriticalPath = isCriticalPathItem(row.criticalPathFlag);
  const isProcoreEntry = row.entryOrigin === "Procore";
  const isClonePending = Boolean(
    isSubmittal &&
      options.cloningEntryIds?.has(String(getProcurementRowIdentity(row))),
  );
  const isPendingApproval =
    isSubmittal && options.pendingApprovalIds?.has(String(row.entryID));
  const isPendingApprovalLocked = isPendingApproval && options.accessLevel >= 2;
  const titleText = isSubmittal
    ? params.value ?? row.specTitle ?? ""
    : row.displayTitle || params.value || "";

  // Toggles a division or subdivision row when the title cell is clicked.
  const handleToggle = (event) => {
    event.stopPropagation();
    if (isGroup) {
      options?.onToggleGroup?.(row);
    }
  };

  const runCellAction = (callback) => (event) => {
    event.stopPropagation();
    callback?.(row);
  };

  return (
    <div
      className={
        isSubmittal
          ? "procurementTitleCell procurementSubmittalCell"
          : "procurementTitleCell"
      }
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        height: "100%",
        cursor: isGroup ? "pointer" : "default",
      }}
      onClick={handleToggle}
    >
      <div
        style={{
          // width: `${baseIndent}px`,
          // minWidth: `${baseIndent}px`,
          height: "100%",
          flexShrink: 0,
        }}
      />

      {renderTreeGuides(row)}

      {isGroup ? (
        <span
          style={{
            color: isDivision ? "#3a6fa8" : "#7b8794",
            display: "inline-flex",
          }}
        >
          {row.isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </span>
      ) : (
        <span style={{ width: 0, display: "inline-block", flexShrink: 0 }} />
      )}

      {isDivision && (
        <FolderFilled style={{ color: "#3a6fa8", fontSize: "14px" }} />
      )}
      {isSubdivision && (
        <FolderFilled style={{ color: "#a6b4c3", fontSize: "13px" }} />
      )}
      {/* {isSubmittal && (
        <FileTextOutlined style={{ color: "#6b7280", fontSize: "13px" }} />
      )} */}

      {isPendingApproval ? (
        <Tooltip
          title="Item is pending approval"
          placement="top"
          color="#1777ff"
        >
          <span
            className="procurementTitleText"
            style={{
              fontSize: isDivision ? "14px" : "13px",
              fontWeight: isDivision ? 700 : isSubdivision ? 600 : 400,
              color: isPendingApprovalLocked ? "#9ca3af" : "#2d3748",
              fontStyle: isPendingApprovalLocked ? "italic" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            {/* <PauseCircleFilled style={{ fontSize: "13px", flexShrink: 0 }} /> */}

            {titleText}
          </span>
        </Tooltip>
      ) : (
        <span
          className="procurementTitleText"
          style={{
            fontSize: isDivision ? "14px" : "13px",
            fontWeight: isDivision ? 700 : isSubdivision ? 600 : 400,
            color: isDivision
              ? "#1f2937"
              : isSubdivision
                ? "#4b5563"
                : "#2d3748",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {titleText}
        </span>
      )}

      {isPendingApproval && options.accessLevel === 1 && (
        <Tooltip title="A change to this row is awaiting manager approval. The approved values may remain visible until approval.">
          <span className="procurementApprovalBadge">
            <ClockCircleOutlined />
            <span>Awaiting approval</span>
          </span>
        </Tooltip>
      )}

      {isSubmittal && (
        <div className="procurementSubmittalActions">
          {options.accessLevel >= 2 && (
            <>
              <Tooltip
                placement="left"
                title={
                  isCriticalPath
                    ? "Remove from Critical Path"
                    : "Add to Critical Path"
                }
              >
                <button
                  type="button"
                  className="procurementSubmittalActionButton"
                  onClick={runCellAction(options?.onToggleCriticalPath)}
                >
                  {isCriticalPath ? <FlagFilled /> : <FlagOutlined />}
                </button>
              </Tooltip>
              <Tooltip title={isClonePending ? "Cloning" : "Clone"}>
                <button
                  type="button"
                  className="procurementSubmittalActionButton"
                  onClick={runCellAction(options?.onCloneSubmittal)}
                  disabled={isClonePending}
                >
                  {isClonePending ? <LoadingOutlined spin /> : <CopyOutlined />}
                </button>
              </Tooltip>
              <Tooltip title="Assign">
                <button
                  type="button"
                  className="procurementSubmittalActionButton"
                  onClick={runCellAction(options?.onOpenAssignDrawer)}
                >
                  <UserAddOutlined />
                </button>
              </Tooltip>
              <Tooltip
                title={isProcoreEntry ? "Hide" : "Delete"}
                placement="right"
              >
                <button
                  type="button"
                  className="procurementSubmittalActionButton"
                  onClick={runCellAction(options?.onDeleteOrHideRow)}
                >
                  {isProcoreEntry ? (
                    <EyeInvisibleOutlined />
                  ) : (
                    <DeleteOutlined />
                  )}
                </button>
              </Tooltip>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Calculates how many days remain until a target date.
function getDaysUntilDate(value) {
  if (!value) return null;

  let parsedDate = null;

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [month, day, year] = value.split("-");
    parsedDate = dayjs(`${year}-${month}-${day}`);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    parsedDate = dayjs(value);
  } else {
    parsedDate = dayjs(value);
  }

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.startOf("day").diff(dayjs().startOf("day"), "day");
}

// Checks whether a cell contains a meaningful non-empty value.
export function hasCellValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

// Resolves the warning state for a field based on the current row values.
function getWarningStatusForField(field, row) {
  if (!row || hasCellValue(row.actualDeliveryDate)) {
    return "";
  }

  if (field === "actualDeliveryDate") {
    return row.actualDeliveryDateStatus || "";
  }

  if (field === "subCommittedDeliveryDate") {
    return row.subCommittedDeliveryDateStatus || "";
  }

  if (field === "projectedDeliveryDate") {
    return row.projectedDeliveryDateStatus === "late" ? "late" : "";
  }

  if (field === "submittalReturned") {
    if (hasCellValue(row.submittalReturned)) {
      return "";
    }

    const diffInDays = getDaysUntilDate(row.submittalApprovalDue);
    if (diffInDays === null || diffInDays > 7) {
      return "";
    }

    return diffInDays > 0 ? "pending" : "late";
  }

  if (field === "submittalIssued") {
    if (hasCellValue(row.submittalIssued)) {
      return "";
    }

    const diffInDays = getDaysUntilDate(row.submittalRequired);
    if (diffInDays === null || diffInDays > 7) {
      return "";
    }

    return diffInDays > 0 ? "pending" : "late";
  }

  if (field === "contractIssuedRelease") {
    if (hasCellValue(row.contractIssuedRelease)) {
      return "";
    }

    const diffInDays = getDaysUntilDate(row.issueContract);
    if (diffInDays === null || diffInDays > 7) {
      return "";
    }

    return diffInDays > 0 ? "pending" : "late";
  }

  return "";
}

// Returns the visible warning status for a submittal cell.
export function getWarningStatus(field, row) {
  if (!row || row.rowType !== "submittal") {
    return "";
  }

  return getWarningStatusForField(field, row);
}

// Returns every status bucket a submittal contributes to in group summaries.
export function getSubmittalRollupStatuses(row) {
  if (!row) {
    return [];
  }

  if (hasCellValue(row.actualDeliveryDate)) {
    return ["completed"];
  }

  const warningStatuses = WARNING_STATUS_FIELDS.map((field) =>
    getWarningStatusForField(field, row),
  );
  const statuses = [];

  if (warningStatuses.includes("late")) {
    statuses.push("overdue");
  } else if (warningStatuses.includes("pending")) {
    statuses.push("pending");
  }

  if (!hasCellValue(row.submittalIssued)) {
    statuses.push("draft");
  }

  return statuses;
}

// Returns the primary rollup status for callers that still expect a single value.
export function getSubmittalRollupStatus(row) {
  return getSubmittalRollupStatuses(row)[0] || "";
}

// Builds the shared cell class names for alignment and read-only styling.
function getCellClassNames(
  params,
  field,
  isTitleColumn,
  isEditable,
  isFormula,
) {
  if (isTitleColumn) {
    return "";
  }

  const classes = ["procurementCenteredCell"];

  if (params.data?.rowType !== "submittal") {
    classes.push("procurementGroupPlaceholderCell");
    return classes.join(" ");
  }

  if (!isEditable || isFormula) {
    classes.push("procurementReadonlyCell");
  }

  return classes.join(" ");
}

// Renders the full-width division and subdivision group rows.
export function ProcurementGroupFullWidthRow(props) {
  const row = props.data;

  if (!row) {
    return null;
  }

  if (row.rowType === "subdivisionAddRow") {
    return (
      <button
        type="button"
        className="procurementSubdivisionAddRow"
        onClick={() => props.onAddSubmittal?.(row)}
      >
        <span className="procurementSubdivisionAddRowIcon">
          <PlusOutlined />
        </span>
        <span>Add Item</span>
      </button>
    );
  }

  const isDivision = row.rowType === "division";
  const isSubdivision = row.rowType === "subdivision";
  const isGroup = isDivision || isSubdivision;

  if (!isGroup) {
    return null;
  }

  const summaryItems = GROUP_SUMMARY_META.map((item) => ({
    ...item,
    count: row.summaryCounts?.[item.key] || 0,
  }));
  const checkState = props.getCheckState?.(row.id, row.rowType) || "none";
  const isSelected = props.selectedIds?.has?.(row.id);
  const isSelectionDisabled = props.isSelectionDisabled?.(row.id, row.rowType);

  return (
    <div
      className={`procurementFullWidthGroupRow ${
        isDivision
          ? "procurementFullWidthDivisionRow"
          : "procurementFullWidthSubdivisionRow"
      } ${isSelected ? "procurementFullWidthGroupRowSelected" : ""}`}
      onClick={() => props.onToggleGroup?.(row)}
    >
      <div
        className="procurementFullWidthGroupLeft"
        style={{
          paddingLeft: row.level === 0 ? 12 : 36,
        }}
      >
        <SelectionCheckboxControl
          state={checkState}
          disabled={isSelectionDisabled}
          onToggle={() => props.onToggleSelect?.(row.id, row.rowType)}
        />

        <span className="procurementFullWidthGroupToggle">
          {row.isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </span>

        {isDivision ? (
          <FolderFilled className="procurementFullWidthDivisionIcon" />
        ) : (
          <FolderFilled className="procurementFullWidthSubdivisionIcon" />
        )}

        <span className="procurementFullWidthGroupTitle">
          {row.displayTitle || ""}
        </span>
      </div>

      <div className="procurementGroupSummary">
        {isSubdivision && props.accessLevel >= 2 && (
          <button
            type="button"
            className="procurementGroupActionButton"
            onClick={(event) => {
              event.stopPropagation();
              props.onAddSubmittal?.(row);
            }}
          >
            <PlusOutlined />
            <span>Add Item</span>
          </button>
        )}

        {summaryItems.map((item) => (
          <span
            key={item.key}
            className={`procurementGroupSummaryBadge ${item.className}`}
          >
            {item.label} {item.count}
          </span>
        ))}
      </div>
    </div>
  );
}

// Builds the AG Grid column definitions for the procurement log.
export function buildColumnDefs(alignedData, options = {}) {
  if (!alignedData) return [];

  const { accessLevel = 0 } = options.columnPermissions || {};

  const pendingApprovalIds = new Set(
    (options.itemsPendingApprovals || []).map(String),
  );
  const enrichedOptions = { ...options, pendingApprovalIds, accessLevel };

  // 1. Map all columns first
  const allMappedColumns = Object.entries(alignedData)
    .filter(([, col]) => col.hidden === "false")
    .map(([key, col]) => {
      const isEditable = col.editable === "true";
      const isFormula = !!col.formula;
      const isTitleColumn = key === "specTitle";
      const sizing = getColumnSizing(key, col);
      const {
        unlockedColumns = null,
        accessLevel = 0,
        assignedEntryIds = null,
        hideSubRequiredDate = false,
      } = options.columnPermissions || {};

      const isPermissionLocked =
        accessLevel === 1 ? !unlockedColumns?.has(key) : false;

      const colDef = {
        field: key,
        headerName: col.label,
        flex: sizing.flex,
        minWidth: sizing.minWidth,
        maxWidth: sizing.maxWidth,
        cellRenderer: (params) => {
          const value = params.valueFormatted ?? params.value ?? "";
          const workflowLockState =
            key === "actualDeliveryDate" && params.data?.rowType === "submittal"
              ? getWorkflowLockState(enrichedOptions, params.data)
              : EMPTY_WORKFLOW_LOCK_STATE;
          const workflowLockTooltip =
            renderWorkflowLockTooltipContent(workflowLockState);

          const content =
            workflowLockState.isLocked &&
            params.data?.rowType === "submittal" &&
            key === "actualDeliveryDate" ? (
              <Tooltip
                title={workflowLockTooltip}
                placement="top"
                color="#1f1f1f"
              >
                <span
                  style={{
                    width: "100%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                >
                  <span>{value}</span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      color: "#94a3b8",
                      fontSize: 12,
                    }}
                  >
                    <LockOutlined />
                  </span>
                </span>
              </Tooltip>
            ) : (
              <span
                style={{
                  width: "100%",
                  display: "inline-block",
                }}
              >
                {value}
              </span>
            );

          return withPendingApprovalTooltip(
            renderCellWithEditingPresence(
              renderCellWithEditFeedback(content, params, key, enrichedOptions),
              params,
              key,
              enrichedOptions,
            ),
            params,
            enrichedOptions.pendingApprovalIds,
          );
        },
        cellRendererParams: {
          suppressMouseEventHandling: ({ event }) =>
            shouldSuppressTitleMouseHandling(event),
        },
        tooltipValueGetter: (params) => {
          if (
            getCellEditFeedback(enrichedOptions, params.data, key)?.status ===
            "unconfirmed"
          ) {
            return `${UNCONFIRMED_EDIT_MESSAGE} Your entered value is still shown.`;
          }
          // scheduleID warning tooltip
          if (key === "scheduleID" && params.data?.rowType === "submittal") {
            return params.data?.scheduleSyncWarning || null;
          }

          // Pending approval tooltip on every cell in the row
          if (
            params.data?.rowType === "submittal" &&
            enrichedOptions.pendingApprovalIds?.has(
              String(params.data?.entryID),
            )
          ) {
            return "Item is pending approval";
          }

          if (key === "actualDeliveryDate") {
            const workflowLockState = getWorkflowLockState(
              enrichedOptions,
              params.data,
            );

            if (workflowLockState.isLocked) {
              return null;
            }
          }

          return null;
        },
        cellClassRules: {
          procurementEditWorking: (params) =>
            ["saving", "submitting"].includes(
              getCellEditFeedback(enrichedOptions, params.data, key)?.status,
            ),
          procurementEditSaved: (params) =>
            getCellEditFeedback(enrichedOptions, params.data, key)?.status ===
            "saved",
          procurementEditSubmitted: (params) =>
            getCellEditFeedback(enrichedOptions, params.data, key)?.status ===
            "submitted",
          procurementEditUnconfirmed: (params) =>
            getCellEditFeedback(enrichedOptions, params.data, key)?.status ===
            "unconfirmed",
          procurementRemoteEditingCell: (params) =>
            Boolean(
              getEditingPresenceForCell(enrichedOptions, params.data, key),
            ),
          ...(isTitleColumn
            ? {}
            : {
                procurementWarningPending: (params) =>
                  getWarningStatus(key, params.data) === "pending",
                procurementWarningLate: (params) =>
                  getWarningStatus(key, params.data) === "late",
              }),
        },
        headerClass: isTitleColumn ? undefined : "procurementCenteredHeader",
        headerComponent: FilterIconHeader,
        headerComponentParams: {
          displayName: col.label,
          description: col.description,
          centered: !isTitleColumn,
          sortState:
            options.sortModel?.colId === key ? options.sortModel.sort : null,
          onToggleSort: () => options.onToggleSort?.(key),
          // Filter props
          field: key,
          filterType: getColumnFilterType(col),
          columnFilterModel: options.columnFilterModel,
          onSetColumnFilter: options.onSetColumnFilter,
        },
        editable: (params) => {
          if (params.data?.rowType !== "submittal") return false;
          if (!isEditable || isFormula || isPermissionLocked) return false;

          const isPending = enrichedOptions.pendingApprovalIds?.has(
            String(params.data?.entryID),
          );
          const isPendingLocked = isPending && accessLevel >= 2;
          if (isPendingLocked) return false;

          if (
            assignedEntryIds &&
            !assignedEntryIds.has(String(params.data?.entryID))
          )
            return false;

          if (
            key === "actualDeliveryDate" &&
            getWorkflowLockState(enrichedOptions, params.data).isLocked
          ) {
            return false;
          }

          return true;
        },
        // 2. Style permission-locked columns like read-only ones
        cellClass: (params) => {
          const entryId = String(params.data?.entryID);
          const isPendingApproval =
            enrichedOptions.pendingApprovalIds?.has(entryId);
          const isPendingApprovalLocked = isPendingApproval && accessLevel >= 2;
          const isAssignmentLocked = assignedEntryIds
            ? !assignedEntryIds.has(entryId)
            : false;
          const isWorkflowLocked =
            key === "actualDeliveryDate" &&
            getWorkflowLockState(enrichedOptions, params.data).isLocked;

          return getCellClassNames(
            params,
            key,
            isTitleColumn,
            isEditable &&
              !isPermissionLocked &&
              !isPendingApprovalLocked &&
              !isAssignmentLocked &&
              !isWorkflowLocked,
            isFormula,
          );
        },
        valueSetter: (params) => {
          if (params.data?.rowType !== "submittal") {
            return false;
          }

          if (
            key === "specNumber" &&
            params.newValue?.[SUBMITTAL_DROPDOWN_SELECTION_MARKER]
          ) {
            params.data.specNumber =
              params.newValue.sourceRow?.specNumber ?? "";
            return true;
          }

          if (params.data?.[key] === params.newValue) {
            return false;
          }

          params.data[key] = params.newValue;
          return true;
        },
        valueGetter: (params) => {
          if (params.data?.rowType !== "submittal") {
            return isTitleColumn ? params.data?.displayTitle || "" : "";
          }
          const feedback = getCellEditFeedback(enrichedOptions, params.data, key);
          return feedback ? feedback.value : params.data?.[key];
        },
        suppressKeyboardEvent: (params) =>
          key === "specNumber" &&
          params.editing &&
          params.event?.key === "Enter",
      };

      if (key === "submittalRequired" && hideSubRequiredDate) {
        colDef.valueGetter = () => "";
        colDef.editable = () => false;
        colDef.cellClass = (params) =>
          getCellClassNames(params, key, false, false, false);
      }

      if (col.type === "date") {
        colDef.valueFormatter = (params) => {
          if (!params.value) return "";
          const formattedValue = dayjs(params.value);
          return formattedValue.isValid()
            ? formattedValue.format("MM-DD-YYYY")
            : params.value;
        };
      }

      if (key === "scheduleID") {
        colDef.valueFormatter = (params) =>
          formatScheduleIdDisplay(params.value, options.scheduleOptions);
      }

      // Handle Editors
      if (isEditable && !isFormula && !isPermissionLocked) {
        colDef.cellEditor =
          key === "specNumber"
            ? createSubmittalNumberCellEditor(options.submittalsDropDown)
            : key === "scheduleID"
              ? createScheduleIdCellEditor(options.scheduleOptions)
              : col.type === "date"
                ? createDateCellEditor(
                    options.workingDays,
                    options.nonWorkingDays,
                  )
                : getCellEditor(col.type);
      }

      if (isTitleColumn) {
        colDef.cellRenderer = (params) =>
          renderCellWithEditingPresence(
            renderCellWithEditFeedback(
              HierarchyTitleCell(params, enrichedOptions),
              params,
              key,
              enrichedOptions,
            ),
            params,
            key,
            enrichedOptions,
          );
        colDef.cellRendererParams = {
          suppressMouseEventHandling: ({ event }) =>
            shouldSuppressTitleMouseHandling(event),
        };
        colDef.headerComponent = HierarchyHeader;
        colDef.headerComponentParams = {
          displayName: col.label,
          description: col.description,
          allExpanded: options.allExpanded,
          onToggleAllGroups: options.onToggleAllGroups,
          sortState:
            options.sortModel?.colId === key ? options.sortModel.sort : null,
          onToggleSort: () => options.onToggleSort?.(key),
        };
      }

      return { ...colDef, originalIndex: parseInt(col.index) };
    });

  const specTitle = allMappedColumns.find((c) => c.field === "specTitle");
  const specNumber = allMappedColumns.find((c) => c.field === "specNumber");
  const selectionColumn = {
    field: "_rowSelection",
    colId: "_rowSelection",
    headerName: "",
    description: "",
    minWidth: 44,
    maxWidth: 44,
    width: 44,
    flex: 0,
    pinned: "left",
    lockPinned: true,
    lockPosition: "left",
    suppressMovable: true,
    resizable: false,
    editable: false,
    sortable: false,
    headerClass: "procurementCenteredHeader",
    headerComponent: () => null,
    headerComponentParams: {
      displayName: "",
      description: "",
      centered: true,
    },
    cellClass: "procurementCenteredCell",
    valueGetter: () => "",
    cellRenderer: (params) => {
      const row = params.data;

      if (!row || row.rowType !== "submittal") {
        return null;
      }

      return (
        <SelectionCheckboxControl
          state={options.getCheckState?.(row.id, row.rowType) || "none"}
          disabled={options.isSelectionDisabled?.(row.id, row.rowType)}
          onToggle={() => options.onToggleSelect?.(row.id, row.rowType)}
        />
      );
    },
  };
  const entryOriginColumn = {
    field: "entryOriginIndicator",
    colId: "entryOriginIndicator",
    headerName: "",
    description: "",
    minWidth: 42,
    maxWidth: 42,
    width: 42,
    flex: 0,
    pinned: "left",
    lockPinned: true,
    lockPosition: "left",
    suppressMovable: true,
    resizable: false,
    editable: false,
    sortable: false,
    headerClass: "procurementCenteredHeader procurementHiddenHeaderCell",
    headerComponent: () => null,
    headerComponentParams: {
      displayName: "",
      description: "",
      centered: true,
    },
    cellClass: (params) =>
      getCellClassNames(params, "entryOrigin", false, false, false),
    valueGetter: (params) =>
      params.data?.rowType === "submittal"
        ? params.data?.entryOrigin || ""
        : "",
    cellRenderer: EntryOriginCell,
  };

  const otherColumns = allMappedColumns
    .filter(
      (c) =>
        c.field !== "specTitle" &&
        c.field !== "specNumber" &&
        c.field !== "entryOrigin",
    )
    .sort((a, b) => a.originalIndex - b.originalIndex);

  if (specTitle) {
    specTitle.pinned = "left";
    specTitle.lockPinned = true;
    specTitle.lockPosition = "left";
    specTitle.suppressMovable = true;
  }
  // if (specNumber) specNumber.pinned = "left";

  const finalCols = [];
  finalCols.push(selectionColumn);
  finalCols.push(entryOriginColumn);
  if (specTitle) finalCols.push(specTitle);
  if (specNumber) finalCols.push(specNumber);

  if (!specTitle) {
    finalCols.splice(1, 0, {
      field: "specTitle",
      headerName: "Submittal Title",
      pinned: "left",
      lockPinned: true,
      lockPosition: "left",
      suppressMovable: true,
      minWidth: 440,
      flex: 1.9,
      sortable: false,
      editable: false,
      cellRendererParams: {
        suppressMouseEventHandling: ({ event }) =>
          shouldSuppressTitleMouseHandling(event),
      },
      headerComponent: HierarchyHeader,
      headerComponentParams: {
        displayName: "Submittal Title",
        description: "Submittal Title",
        allExpanded: options.allExpanded,
        onToggleAllGroups: options.onToggleAllGroups,
        sortState:
          options.sortModel?.colId === "specTitle"
            ? options.sortModel.sort
            : null,
        onToggleSort: () => options.onToggleSort?.("specTitle"),
      },
      cellClassRules: {
        procurementRemoteEditingCell: (params) =>
          Boolean(
            getEditingPresenceForCell(
              enrichedOptions,
              params.data,
              "specTitle",
            ),
          ),
      },
      cellRenderer: (params) =>
        renderCellWithEditingPresence(
          HierarchyTitleCell(params, enrichedOptions),
          params,
          "specTitle",
          enrichedOptions,
        ),
      valueGetter: (params) => params.data?.displayTitle || "",
    });
  }

  return [...finalCols, ...otherColumns];
}
